import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import { useTheme } from "@/context/ThemeContext";
import { APP_URL } from "@/lib/urls";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Features", to: "/features" },
  { label: "For doctors", to: "/doctors" },
  { label: "Solutions", to: "/solutions" },
  { label: "How it works", to: "/how-it-works" },
  { label: "Pricing", to: "/pricing" },
  { label: "Security", to: "/security" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { theme, toggle } = useTheme();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <motion.header
        data-testid="navbar"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border-color] duration-500 ${
          scrolled
            ? "glass-strong border-b hairline"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-14 lg:h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo size="sm" />
            <nav className="hidden lg:flex items-center gap-0.5">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `relative px-2.5 py-1.5 text-[13px] font-medium tracking-tight transition-colors ${
                      isActive
                        ? "text-foreground"
                        : "text-muted-foreground hover:text-foreground"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {n.label}
                      {isActive && (
                        <motion.span
                          layoutId="nav-underline"
                          className="absolute left-2.5 right-2.5 -bottom-0.5 h-px bg-brand-gradient"
                          transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              onClick={toggle}
              data-testid="theme-toggle"
              aria-label="Toggle color theme"
              className="hidden sm:inline-flex h-8 w-8 items-center justify-center rounded-full hairline hover:bg-secondary transition-colors"
            >
              {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            </button>
            <a
              href={APP_URL}
              data-testid="nav-sign-in"
              className="hidden md:inline-flex text-[13px] font-medium px-3 py-1.5 rounded-full hover:bg-secondary transition-colors"
            >
              Sign in
            </a>
            {/* Mobile: Login → app */}
            <a
              href={APP_URL}
              data-testid="nav-login-mobile"
              className="md:hidden group inline-flex items-center gap-1.5 text-[13px] font-semibold pl-3.5 pr-2.5 py-1.5 rounded-full bg-foreground text-background hover:opacity-90 transition-opacity"
            >
              Login
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            {/* Desktop: Request demo */}
            <Link
              to="/request-demo"
              data-testid="nav-request-demo"
              className="hidden md:inline-flex group items-center gap-1.5 text-[13px] font-semibold pl-3.5 pr-2.5 py-1.5 rounded-full bg-foreground text-background hover:opacity-90 transition-opacity"
            >
              Request demo
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
              className="lg:hidden h-8 w-8 inline-flex items-center justify-center rounded-full hairline"
            >
              {open ? <X className="h-3.5 w-3.5" /> : <Menu className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden pt-20"
          >
            <div className="absolute inset-0 glass-strong" onClick={() => setOpen(false)} />
            <motion.nav
              initial={{ y: -12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="relative max-w-7xl mx-auto px-6 py-6 flex flex-col gap-1"
            >
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="text-2xl font-heading font-medium py-3 border-b hairline"
                >
                  {n.label}
                </Link>
              ))}
              <div className="mt-6 flex gap-2">
                <button
                  onClick={toggle}
                  className="h-11 px-4 rounded-full hairline text-sm inline-flex items-center gap-2"
                >
                  {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  Theme
                </button>
                <a
                  href={APP_URL}
                  data-testid="mobile-nav-login"
                  className="flex-1 h-11 inline-flex items-center justify-center gap-1.5 rounded-full bg-foreground text-background text-sm font-semibold"
                >
                  Login <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
